import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import SupplierManagementShell from "../supplier/SupplierManagementShell";
import "../supplier/supplierManagementTables.css";
import MultiSelectDropdown from "../../../../globalComponents/form/dropdowns/MultiSelectDropdown";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import {
  tableCardSurface,
  thStyle,
  primaryBtn,
  secondaryBtn,
  statusBadge,
} from "./assessmentStyles";
import AssessmentFilterBar from "./AssessmentFilterBar";
import AssessmentEmptyState from "./AssessmentEmptyState";
import {
  applyAssessmentFilters,
  buildLookupOptions,
} from "./assessmentFilters";

const OWNER_OPTIONS = [
  { value: "RIU_TEMPLATE", label: "RIU Template" },
  { value: "COMPANY_CUSTOM", label: "Custom" },
];

const STATUS_VARIANT = {
  DRAFT: "warning",
  PUBLISHED: "success",
  ARCHIVED: "muted",
};

/**
 * /assessment/all — every assessment the company can see: all RIU templates +
 * any assessments this company has created. Backend already enforces the
 * union via viewer_type=company; we just filter client-side by owner_type.
 */
const AllAssessments = () => {
  const history = useHistory();
  const location = useLocation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [ownerFilter, setOwnerFilter] = useState([
    "RIU_TEMPLATE",
    "COMPANY_CUSTOM",
  ]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [industryFilter, setIndustryFilter] = useState([]);
  const [subIndustryFilter, setSubIndustryFilter] = useState([]);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const [industryNames, setIndustryNames] = useState({});
  const [subIndustryNames, setSubIndustryNames] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [templatesRes, ownRes, iRes] = await Promise.all([
        AssessmentService.listAssessments({
          limit: 200,
          owner_type: "RIU_TEMPLATE",
        }),
        AssessmentService.listAssessments({
          limit: 200,
          owner_type: "COMPANY_CUSTOM",
        }),
        AssessmentService.listIndustries(),
      ]);
      setLoading(false);
      const templates = templatesRes?.isSuccess
        ? templatesRes?.data?.data?.items ?? []
        : [];
      const own = ownRes?.isSuccess ? ownRes?.data?.data?.items ?? [] : [];
      setRows([...own, ...templates]);
      const industryList = iRes?.data?.data ?? iRes?.data ?? [];
      if (Array.isArray(industryList)) {
        const indMap = {};
        const subMap = {};
        industryList.forEach((row) => {
          indMap[row.industry_type_id] = row.industry_type_name;
          (row.items || []).forEach((sub) => {
            subMap[sub.id] = sub.title;
          });
        });
        setIndustryNames(indMap);
        setSubIndustryNames(subMap);
      }
    })();
  }, []);

  const industryOptions = useMemo(
    () => buildLookupOptions(rows, "industry", industryNames),
    [rows, industryNames]
  );
  const subIndustryOptions = useMemo(
    () => buildLookupOptions(rows, "sub_industry", subIndustryNames),
    [rows, subIndustryNames]
  );

  const filtered = useMemo(
    () =>
      applyAssessmentFilters(rows, {
        search,
        statusFilter,
        statusField: "status_key",
        dateRange,
        dateField: "updated_at",
        lookups: [
          { values: ownerFilter, field: "owner_type_key" },
          { values: industryFilter, field: "industry" },
          { values: subIndustryFilter, field: "sub_industry" },
        ],
      }),
    [
      rows,
      ownerFilter,
      industryFilter,
      subIndustryFilter,
      search,
      statusFilter,
      dateRange,
    ]
  );

  const actions = (
    <>
      <button
        type="button"
        style={secondaryBtn}
        onClick={() => history.push("/assessment/create")}
      >
        + Create
      </button>
      <button
        type="button"
        style={primaryBtn}
        onClick={() => history.push("/assessment/assign")}
      >
        Assign
      </button>
    </>
  );

  return (
    <SupplierManagementShell
      pathname={location.pathname}
      pageTitle="All Assessments"
      totalCount={filtered.length}
      actionsSlot={actions}
    >
      <AssessmentFilterBar
        rows={rows}
        statusField="status_key"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search title…"
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        dateLabel="Updated"
        extraFilters={
          <>
            <div style={{ width: 220 }}>
              <MultiSelectDropdown
                options={OWNER_OPTIONS}
                selected={ownerFilter}
                onChange={setOwnerFilter}
                placeholder="Owner…"
                searchPlaceholder="Search owner type"
                showSelectAll
              />
            </div>
            <div style={{ width: 220 }}>
              <MultiSelectDropdown
                options={industryOptions}
                selected={industryFilter}
                onChange={setIndustryFilter}
                placeholder="Industry…"
                searchPlaceholder="Search industry"
                showSelectAll
              />
            </div>
            <div style={{ width: 220 }}>
              <MultiSelectDropdown
                options={subIndustryOptions}
                selected={subIndustryFilter}
                onChange={setSubIndustryFilter}
                placeholder="Sub-industry…"
                searchPlaceholder="Search sub-industry"
                showSelectAll
              />
            </div>
          </>
        }
      />

      <div className="supplier-mgmt-table-wrap" style={tableCardSurface}>
        <div className="supplier-mgmt-table-scroll">
          <table className="supplier-mgmt-table table">
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 50 }}>#</th>
                <th style={thStyle}>Title</th>
                <th style={{ ...thStyle, width: 140 }}>Owner</th>
                <th style={thStyle}>Industry</th>
                <th style={thStyle}>Sub-industry</th>
                <th style={{ ...thStyle, width: 110 }}>Status</th>
                <th style={{ ...thStyle, width: 110 }}>Updated</th>
                <th
                  style={{ ...thStyle, width: 90, textAlign: "right" }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 0 }}>
                    <AssessmentEmptyState
                      hasFilters={
                        Boolean(search) ||
                        statusFilter.length > 0 ||
                        industryFilter.length > 0 ||
                        subIndustryFilter.length > 0 ||
                        ownerFilter.length < 2 ||
                        Boolean(dateRange.from || dateRange.to)
                      }
                      onClearFilters={() => {
                        setSearch("");
                        setStatusFilter([]);
                        setIndustryFilter([]);
                        setSubIndustryFilter([]);
                        setOwnerFilter(["RIU_TEMPLATE", "COMPANY_CUSTOM"]);
                        setDateRange({ from: "", to: "" });
                      }}
                    />
                  </td>
                </tr>
              ) : (
                filtered.map((a, idx) => {
                  const source =
                    a.owner_type_key === "COMPANY_CUSTOM"
                      ? "company"
                      : "riu_backend";
                  const viewHref = `/assessment/${a.id}?source=${source}`;
                  return (
                  <tr key={`${a.owner_type_key || "?"}:${a.id}`}>
                    <td>{idx + 1}</td>
                    <td>
                      <Link to={viewHref}>{a.title}</Link>
                    </td>
                    <td>
                      <span
                        style={statusBadge(
                          a.owner_type_key === "RIU_TEMPLATE"
                            ? "info"
                            : "muted"
                        )}
                      >
                        {a.owner_type_key === "RIU_TEMPLATE"
                          ? "TEMPLATE"
                          : "CUSTOM"}
                      </span>
                    </td>
                    <td>{industryNames[a.industry] || a.industry}</td>
                    <td>
                      {subIndustryNames[a.sub_industry] || a.sub_industry}
                    </td>
                    <td>
                      <span
                        style={statusBadge(
                          STATUS_VARIANT[a.status_key] || "info"
                        )}
                      >
                        {a.status_key}
                      </span>
                    </td>
                    <td>{a.updated_at?.slice(0, 10)}</td>
                    <td style={{ textAlign: "right" }}>
                      <Link
                        to={viewHref}
                        style={{
                          ...secondaryBtn,
                          height: 28,
                          padding: "0 10px",
                          fontSize: 12,
                          display: "inline-block",
                          lineHeight: "26px",
                          textDecoration: "none",
                        }}
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SupplierManagementShell>
  );
};

export default AllAssessments;
